import React, { useState } from 'react'
import {
  Box,
  Container,
  Typography,
  TextField,
  Button,
  Paper,
  Stack,
  CircularProgress
} from '@mui/material'
import TikTokIcon from '@mui/icons-material/MusicVideo'
import useRouter from '../hooks/useRouter'
import VisualizationsFab from '../components/navigation/VisualizationsFab'

const TiktokStart: React.FC = () => {
  const router = useRouter()
  const customer = router.params.customer || ''
  const job = router.params.job || ''
  const [tiktokUrl, setTiktokUrl] = useState('')
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)

  const handleSubmit = () => {
    if (!tiktokUrl.trim()) {
      setError('Please enter a TikTok URL')
      return
    }

    // Basic validation for TikTok URL
    if (!tiktokUrl.includes('tiktok.com')) {
      setError('Please enter a valid TikTok URL')
      return
    }

    // Show loading state
    setLoading(true)

    // Simulate scraping/processing delay
    setTimeout(() => {
      router.navigate('tiktok-analytics', { customer, job })
    }, 5500)
  }

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !loading) {
      handleSubmit()
    }
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: '#f5f5f5'
      }}
    >
      <Container maxWidth="lg">
        <Box sx={{ textAlign: 'center', mb: 3 }}>
          <img
            src="/img/Sky.png"
            alt="Sky Logo"
            style={{
              height: 80,
              marginBottom: 20
            }}
          />
        </Box>
        <Paper
          sx={{
            p: 4,
            bgcolor: '#9c27b0',
            color: 'white',
            maxWidth: 1000,
            mx: 'auto'
          }}
        >
          {!loading ? (
            <Stack spacing={3} alignItems="center">
              <TikTokIcon sx={{ fontSize: 64 }} />

              <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                TikTok Overview
              </Typography>

              <Typography variant="body1" sx={{ opacity: 0.9 }} textAlign="center">
                Enter a TikTok video URL to analyze comments and engagement
              </Typography>

              <Box sx={{ display: 'flex', width: '100%', gap: 2 }}>
                <TextField
                  fullWidth
                  label="TikTok Video URL"
                  placeholder="https://www.tiktok.com/@username/video/..."
                  value={tiktokUrl}
                  onChange={(e) => {
                    setTiktokUrl(e.target.value)
                    setError('')
                  }}
                  onKeyPress={handleKeyPress}
                  error={!!error}
                  helperText={error}
                  sx={{
                    flex: 1,
                    '& .MuiInputBase-root': {
                      bgcolor: 'white'
                    },
                    '& .MuiInputLabel-root': {
                      color: 'rgba(0, 0, 0, 0.6)'
                    },
                    '& .MuiOutlinedInput-root': {
                      '& fieldset': {
                        borderColor: 'rgba(255, 255, 255, 0.5)'
                      },
                      '&:hover fieldset': {
                        borderColor: 'white'
                      }
                    }
                  }}
                />

                <Button
                  variant="contained"
                  size="large"
                  onClick={handleSubmit}
                  disabled={loading}
                  sx={{
                    px: 4,
                    bgcolor: 'white',
                    color: '#9c27b0',
                    '&:hover': {
                      bgcolor: 'rgba(255, 255, 255, 0.9)'
                    },
                    '&:disabled': {
                      bgcolor: 'rgba(255, 255, 255, 0.5)'
                    }
                  }}
                >
                  Analyze
                </Button>
              </Box>

              <Typography variant="caption" sx={{ opacity: 0.8 }}>
                Example: https://www.tiktok.com/@skysportsfootball/video/7507668615685082390
              </Typography>
            </Stack>
          ) : (
            <Stack spacing={3} alignItems="center" sx={{ py: 4 }}>
              <CircularProgress size={64} sx={{ color: 'white' }} />

              <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
                Processing TikTok Video
              </Typography>

              <Stack spacing={1} alignItems="center">
                <Typography variant="body1" sx={{ opacity: 0.9 }}>
                  Scraping comments from TikTok...
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.7 }}>
                  This may take a few seconds
                </Typography>
              </Stack>

              <Typography variant="caption" sx={{ mt: 2, opacity: 0.6 }}>
                Analyzing: {tiktokUrl}
              </Typography>
            </Stack>
          )}
        </Paper>
      </Container>

      {/* FAB Navigation */}
      <VisualizationsFab />
    </Box>
  )
}

export default TiktokStart